import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Bell, Check, Trash2, Filter, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { NotificationItem } from '../components/notifications';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteAllReadNotifications,
} from '../hooks/useNotifications';
import {
  NotificationType,
  getNotificationTypeLabel,
  type NotificationResponse,
  type NotificationFilterRequest,
} from '../types/notifications';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';

type FilterTab = 'all' | 'unread' | 'read';

const PAGE_SIZE = 15;

export function Notifications() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<FilterTab>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(1);

  // Build filter based on tab and type
  const filter: NotificationFilterRequest = {
    page,
    pageSize: PAGE_SIZE,
    isRead: tab === 'all' ? null : tab === 'read',
    type: typeFilter === 'all' ? null : (parseInt(typeFilter) as NotificationType),
  };

  const { data, isLoading, refetch, isRefetching } = useNotifications(filter);
  const notifications = data?.notifications ?? [];
  const totalPages = data?.pageCount ?? 1;
  const totalCount = data?.totalCount ?? 0;
  const unreadCount = data?.unreadCount ?? 0;

  // Mutations
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const deleteAllRead = useDeleteAllReadNotifications();

  const handleNotificationClick = (notification: NotificationResponse) => {
    // Navigate to the action URL if available
    if (notification.actionUrl) {
      if (notification.actionUrl.startsWith('/')) {
        navigate({ to: notification.actionUrl });
      } else {
        // For absolute URLs, extract path and navigate if it's the same origin
        try {
          const url = new URL(notification.actionUrl);
          if (url.origin === window.location.origin) {
            // Same origin - navigate within app
            navigate({ to: url.pathname + url.search + url.hash });
          } else {
            // Different origin - open in new tab
            window.open(notification.actionUrl, '_blank');
          }
        } catch {
          // Invalid URL - try navigating as-is
          navigate({ to: notification.actionUrl });
        }
      }
    }
  };

  const handleMarkAsRead = (id: string) => {
    markAsRead.mutate(id);
  };

  const handleTabChange = (value: string) => {
    setTab(value as FilterTab);
    setPage(1); // Reset to first page on tab change
  };

  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value);
    setPage(1); // Reset to first page on filter change
  };

  // Grouped notification type options for filter dropdown
  const typeGroups = [
    {
      label: 'Ticket Activity',
      options: [
        { value: String(NotificationType.TicketCreated), label: getNotificationTypeLabel(NotificationType.TicketCreated) },
        { value: String(NotificationType.TicketAssigned), label: getNotificationTypeLabel(NotificationType.TicketAssigned) },
        { value: String(NotificationType.TicketStatusChanged), label: getNotificationTypeLabel(NotificationType.TicketStatusChanged) },
        { value: String(NotificationType.TicketUpdated), label: getNotificationTypeLabel(NotificationType.TicketUpdated) },
        { value: String(NotificationType.TicketComment), label: getNotificationTypeLabel(NotificationType.TicketComment) },
      ],
    },
    {
      label: 'Collaboration',
      options: [
        { value: String(NotificationType.ParticipantAdded), label: getNotificationTypeLabel(NotificationType.ParticipantAdded) },
        { value: String(NotificationType.FollowerAdded), label: getNotificationTypeLabel(NotificationType.FollowerAdded) },
      ],
    },
    {
      label: 'Reminders & Alerts',
      options: [
        { value: String(NotificationType.TicketReminder), label: getNotificationTypeLabel(NotificationType.TicketReminder) },
      ],
    },
  ];

  return (
    <div className="space-y-4 p-4 sm:space-y-6 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Notifications
          </h1>
          <p className="text-muted-foreground">
            {unreadCount > 0
              ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
              : 'All caught up!'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw className={cn('h-4 w-4 mr-2', isRefetching && 'animate-spin')} />
            Refresh
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
            >
              <Check className="h-4 w-4 mr-2" />
              Mark all read
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => deleteAllRead.mutate()}
            disabled={deleteAllRead.isPending}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear read
          </Button>
        </div>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Tabs value={tab} onValueChange={handleTabChange}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="unread">
                  Unread
                  {unreadCount > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-xs">
                      {unreadCount}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="read">Read</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
              <Select value={typeFilter} onValueChange={handleTypeFilterChange}>
                <SelectTrigger className="w-full sm:w-[220px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectSeparator />
                  {typeGroups.map((group) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel>{group.label}</SelectLabel>
                      {group.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="divide-y">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-start gap-3 p-4">
                  <Skeleton className="h-5 w-5 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/4" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-12 text-center">
              <Bell className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                {tab === 'unread'
                  ? 'No unread notifications'
                  : tab === 'read'
                  ? 'No read notifications'
                  : 'No notifications yet'}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[50vh] sm:h-[60vh]">
              <div className="divide-y">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClick={handleNotificationClick}
                    onMarkAsRead={handleMarkAsRead}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, totalCount)} of{' '}
            {totalCount}
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground px-2">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
